/**
 * Boli Live - Create Product Page
 *
 * Product creation form for sellers to list fixed-price items.
 */

import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Package,
  Image as ImageIcon,
  DollarSign,
  Truck,
  Loader2,
  Plus,
  X,
  AlertCircle,
} from 'lucide-react';
import { getProductService } from '@boli/core';
import type { AuctionCategory, ProductCondition, Province, CreateProductRequest } from '@boli/types';
import { cn } from '../lib/utils';

// Dropdown options
const CATEGORIES: { value: AuctionCategory; label: string }[] = [
  { value: 'electronics', label: 'Electronics' },
  { value: 'fashion', label: 'Fashion' },
  { value: 'home', label: 'Home & Garden' },
  { value: 'vehicles', label: 'Vehicles' },
  { value: 'collectibles', label: 'Collectibles' },
  { value: 'art', label: 'Art' },
  { value: 'sports', label: 'Sports' },
  { value: 'other', label: 'Other' },
];

const CONDITIONS: { value: ProductCondition; label: string }[] = [
  { value: 'new', label: 'New' },
  { value: 'like_new', label: 'Like New' },
  { value: 'good', label: 'Good' },
  { value: 'fair', label: 'Fair' },
  { value: 'parts', label: 'For Parts' },
];

const PROVINCES: { value: Province; label: string }[] = [
  { value: 'punjab', label: 'Punjab' },
  { value: 'sindh', label: 'Sindh' },
  { value: 'kpk', label: 'Khyber Pakhtunkhwa' },
  { value: 'balochistan', label: 'Balochistan' },
  { value: 'islamabad', label: 'Islamabad Capital Territory' },
  { value: 'gilgit', label: 'Gilgit-Baltistan' },
  { value: 'ajk', label: 'Azad Jammu & Kashmir' },
];

interface FormData {
  title: string;
  description: string;
  images: string[];
  category: AuctionCategory;
  condition: ProductCondition;
  price: string;
  compareAtPrice: string;
  stock: string;
  sku: string;
  weight: string;
  originCity: string;
  originProvince: Province;
}

const initialFormData: FormData = {
  title: '',
  description: '',
  images: [],
  category: 'electronics',
  condition: 'new',
  price: '',
  compareAtPrice: '',
  stock: '1',
  sku: '',
  weight: '',
  originCity: '',
  originProvince: 'punjab',
};

export default function CreateProductPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [imageUrl, setImageUrl] = useState('');
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const updateField = <K extends keyof FormData>(field: K, value: FormData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when field is updated
    if (formErrors[field]) {
      setFormErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const addImage = () => {
    const url = imageUrl.trim();
    if (!url) return;

    // Basic URL validation
    try {
      new URL(url);
      if (formData.images.length < 10) {
        updateField('images', [...formData.images, url]);
        setImageUrl('');
      }
    } catch {
      setFormErrors((prev) => ({ ...prev, imageUrl: 'Please enter a valid URL' }));
    }
  };

  const removeImage = (index: number) => {
    updateField(
      'images',
      formData.images.filter((_: string, i: number) => i !== index)
    );
  };

  const validate = (): boolean => {
    const errors: Record<string, string> = {};

    // Title validation
    if (!formData.title.trim()) {
      errors.title = 'Title is required';
    } else if (formData.title.trim().length < 3) {
      errors.title = 'Title must be at least 3 characters';
    } else if (formData.title.trim().length > 200) {
      errors.title = 'Title must be less than 200 characters';
    }

    // Description validation (optional but max length)
    if (formData.description.length > 5000) {
      errors.description = 'Description must be less than 5000 characters';
    }

    // Images validation
    if (formData.images.length === 0) {
      errors.images = 'At least one image is required';
    }

    // Price validation
    const price = parseInt(formData.price, 10);
    if (!formData.price.trim()) {
      errors.price = 'Price is required';
    } else if (isNaN(price) || price <= 0) {
      errors.price = 'Price must be a positive number';
    }

    // Compare at price validation (optional)
    if (formData.compareAtPrice.trim()) {
      const comparePrice = parseInt(formData.compareAtPrice, 10);
      if (isNaN(comparePrice) || comparePrice <= 0) {
        errors.compareAtPrice = 'Must be a positive number';
      } else if (comparePrice <= price) {
        errors.compareAtPrice = 'Must be higher than price';
      }
    }

    // Stock validation
    const stock = parseInt(formData.stock, 10);
    if (!formData.stock.trim()) {
      errors.stock = 'Stock is required';
    } else if (isNaN(stock) || stock < 0) {
      errors.stock = 'Stock must be 0 or more';
    }

    // Weight validation
    const weight = parseFloat(formData.weight);
    if (!formData.weight.trim()) {
      errors.weight = 'Weight is required';
    } else if (isNaN(weight) || weight <= 0) {
      errors.weight = 'Weight must be a positive number';
    }

    // Origin city validation
    if (!formData.originCity.trim()) {
      errors.originCity = 'City is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const productService = getProductService();

      const createRequest: CreateProductRequest = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        images: formData.images,
        category: formData.category,
        condition: formData.condition,
        price: parseInt(formData.price, 10),
        compareAtPrice: formData.compareAtPrice.trim()
          ? parseInt(formData.compareAtPrice, 10)
          : undefined,
        stock: parseInt(formData.stock, 10),
        sku: formData.sku.trim() || undefined,
        shippingInfo: {
          weight: parseFloat(formData.weight),
          originCity: formData.originCity.trim(),
          originProvince: formData.originProvince,
        },
      };

      const result = await productService.createProduct(createRequest);

      if (result.success) {
        navigate('/seller/products');
      } else {
        setSubmitError(result.error?.message || 'Failed to create product');
      }
    } catch {
      setSubmitError('An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen bg-secondary/30 pb-8"
    >
      {/* Header */}
      <div className="sticky top-0 z-20 bg-background/95 backdrop-blur border-b border-border">
        <div className="max-w-2xl mx-auto flex items-center gap-4 p-4">
          <Link
            to="/seller/products"
            className="p-2 -ml-2 rounded-full hover:bg-secondary transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="text-lg font-bold text-foreground">Add New Product</h1>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="max-w-2xl mx-auto p-4 space-y-6">
        {/* Submit Error */}
        {submitError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-red-800">Error</p>
              <p className="text-sm text-red-600">{submitError}</p>
            </div>
          </div>
        )}

        {/* Basic Information */}
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center gap-3 mb-6">
            <Package className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-bold text-foreground">Basic Information</h2>
          </div>

          <div className="space-y-4">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Title *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => updateField('title', e.target.value)}
                placeholder="Enter product title"
                className={cn(
                  'w-full px-4 py-2 border rounded-md bg-background text-foreground',
                  'focus:outline-none focus:ring-2 focus:ring-primary/50',
                  formErrors.title ? 'border-red-500' : 'border-border'
                )}
              />
              {formErrors.title && (
                <p className="text-xs text-red-500 mt-1">{formErrors.title}</p>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => updateField('description', e.target.value)}
                placeholder="Describe your product..."
                rows={4}
                className={cn(
                  'w-full px-4 py-2 border rounded-md bg-background text-foreground resize-none',
                  'focus:outline-none focus:ring-2 focus:ring-primary/50',
                  formErrors.description ? 'border-red-500' : 'border-border'
                )}
              />
              <div className="flex justify-between mt-1">
                {formErrors.description && (
                  <p className="text-xs text-red-500">{formErrors.description}</p>
                )}
                <p className="text-xs text-muted-foreground ml-auto">
                  {formData.description.length}/5000
                </p>
              </div>
            </div>

            {/* Category & Condition */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Category *
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => updateField('category', e.target.value as AuctionCategory)}
                  className="w-full px-4 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                >
                  {CATEGORIES.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Condition *
                </label>
                <select
                  value={formData.condition}
                  onChange={(e) => updateField('condition', e.target.value as ProductCondition)}
                  className="w-full px-4 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                >
                  {CONDITIONS.map((cond) => (
                    <option key={cond.value} value={cond.value}>
                      {cond.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Images */}
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center gap-3 mb-6">
            <ImageIcon className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-bold text-foreground">Images</h2>
          </div>

          <div className="space-y-4">
            {/* Add Image URL */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Image URL *
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={imageUrl}
                  onChange={(e) => {
                    setImageUrl(e.target.value);
                    if (formErrors.imageUrl) {
                      setFormErrors((prev) => {
                        const next = { ...prev };
                        delete next.imageUrl;
                        return next;
                      });
                    }
                  }}
                  placeholder="https://example.com/image.jpg"
                  className={cn(
                    'flex-1 px-4 py-2 border rounded-md bg-background text-foreground',
                    'focus:outline-none focus:ring-2 focus:ring-primary/50',
                    formErrors.imageUrl ? 'border-red-500' : 'border-border'
                  )}
                />
                <button
                  type="button"
                  onClick={addImage}
                  disabled={formData.images.length >= 10}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
              {formErrors.imageUrl && (
                <p className="text-xs text-red-500 mt-1">{formErrors.imageUrl}</p>
              )}
              {formErrors.images && (
                <p className="text-xs text-red-500 mt-1">{formErrors.images}</p>
              )}
              <p className="text-xs text-muted-foreground mt-1">
                Add up to 10 images. Use Cloudinary or similar services for image hosting.
              </p>
            </div>

            {/* Image Preview Grid */}
            {formData.images.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                {formData.images.map((url, index) => (
                  <div
                    key={index}
                    className="relative aspect-square bg-secondary rounded-lg overflow-hidden group"
                  >
                    <img
                      src={url}
                      alt={`Product ${index + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = '/placeholder-product.jpg';
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="absolute top-1 right-1 p-1 bg-black/60 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    {index === 0 && (
                      <span className="absolute bottom-1 left-1 text-[10px] bg-primary text-primary-foreground px-1.5 py-0.5 rounded">
                        Main
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Pricing & Inventory */}
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center gap-3 mb-6">
            <DollarSign className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-bold text-foreground">Pricing & Inventory</h2>
          </div>

          <div className="space-y-4">
            {/* Price */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Price (PKR) *
                </label>
                <input
                  type="number"
                  value={formData.price}
                  onChange={(e) => updateField('price', e.target.value)}
                  placeholder="0"
                  min="1"
                  className={cn(
                    'w-full px-4 py-2 border rounded-md bg-background text-foreground',
                    'focus:outline-none focus:ring-2 focus:ring-primary/50',
                    formErrors.price ? 'border-red-500' : 'border-border'
                  )}
                />
                {formErrors.price && (
                  <p className="text-xs text-red-500 mt-1">{formErrors.price}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Compare at Price (PKR)
                </label>
                <input
                  type="number"
                  value={formData.compareAtPrice}
                  onChange={(e) => updateField('compareAtPrice', e.target.value)}
                  placeholder="0"
                  min="0"
                  className={cn(
                    'w-full px-4 py-2 border rounded-md bg-background text-foreground',
                    'focus:outline-none focus:ring-2 focus:ring-primary/50',
                    formErrors.compareAtPrice ? 'border-red-500' : 'border-border'
                  )}
                />
                {formErrors.compareAtPrice && (
                  <p className="text-xs text-red-500 mt-1">{formErrors.compareAtPrice}</p>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  Original price to show discount
                </p>
              </div>
            </div>

            {/* Stock & SKU */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Stock Quantity *
                </label>
                <input
                  type="number"
                  value={formData.stock}
                  onChange={(e) => updateField('stock', e.target.value)}
                  placeholder="1"
                  min="0"
                  className={cn(
                    'w-full px-4 py-2 border rounded-md bg-background text-foreground',
                    'focus:outline-none focus:ring-2 focus:ring-primary/50',
                    formErrors.stock ? 'border-red-500' : 'border-border'
                  )}
                />
                {formErrors.stock && (
                  <p className="text-xs text-red-500 mt-1">{formErrors.stock}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  SKU
                </label>
                <input
                  type="text"
                  value={formData.sku}
                  onChange={(e) => updateField('sku', e.target.value)}
                  placeholder="Optional"
                  className="w-full px-4 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Stock keeping unit for your reference
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Shipping */}
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center gap-3 mb-6">
            <Truck className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-bold text-foreground">Shipping</h2>
          </div>

          <div className="space-y-4">
            {/* Weight */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Weight (kg) *
              </label>
              <input
                type="number"
                value={formData.weight}
                onChange={(e) => updateField('weight', e.target.value)}
                placeholder="0.5"
                min="0.01"
                step="0.01"
                className={cn(
                  'w-full px-4 py-2 border rounded-md bg-background text-foreground',
                  'focus:outline-none focus:ring-2 focus:ring-primary/50',
                  formErrors.weight ? 'border-red-500' : 'border-border'
                )}
              />
              {formErrors.weight && (
                <p className="text-xs text-red-500 mt-1">{formErrors.weight}</p>
              )}
            </div>

            {/* Origin City & Province */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Origin City *
                </label>
                <input
                  type="text"
                  value={formData.originCity}
                  onChange={(e) => updateField('originCity', e.target.value)}
                  placeholder="Karachi"
                  className={cn(
                    'w-full px-4 py-2 border rounded-md bg-background text-foreground',
                    'focus:outline-none focus:ring-2 focus:ring-primary/50',
                    formErrors.originCity ? 'border-red-500' : 'border-border'
                  )}
                />
                {formErrors.originCity && (
                  <p className="text-xs text-red-500 mt-1">{formErrors.originCity}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Origin Province *
                </label>
                <select
                  value={formData.originProvince}
                  onChange={(e) => updateField('originProvince', e.target.value as Province)}
                  className="w-full px-4 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                >
                  {PROVINCES.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex gap-3">
          <Link
            to="/seller/products"
            className="flex-1 py-3 border border-border rounded-md font-semibold text-center hover:bg-muted transition-colors"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={isSubmitting}
            className={cn(
              'flex-1 py-3 rounded-md font-semibold flex items-center justify-center gap-2 transition-colors',
              'bg-primary text-primary-foreground hover:bg-primary/90',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Creating...
              </>
            ) : (
              'Create Product'
            )}
          </button>
        </div>
      </form>
    </motion.div>
  );
}
